import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { loadStaticData, useLiveDb } from '@/lib/offlineData';

export interface InternationalCategoryComparison {
  category: string;
  country_code: string;
  scenario_count: number;
  avg_mapped_codes: number;
  avg_intl_cost: number;
  avg_us_national_cost: number;
  us_vs_intl_multiplier: number;
}

export interface InternationalOverallComparison {
  country_code: string;
  scenario_count: number;
  avg_intl_cost: number;
  avg_us_national_cost: number;
  us_vs_intl_multiplier: number;
}

export interface InternationalCountry {
  country_code: string;
  country_name: string;
  flag_emoji: string | null;
  healthcare_system: string | null;
}

export interface InternationalScenarioCost {
  scenario_id: string;
  country_code: string;
  mapped_cpt_codes: number;
  total_cpt_codes: number;
  intl_total_cost: number;
  us_national_total_cost: number;
}

export interface InternationalProcedurePrice {
  us_code: string;
  country_code: string;
  price_usd: number;
}

export interface InternationalProcedureName {
  code: string;
  llm_quick_name: string | null;
  name: string;
}

export interface InternationalUsPricing {
  cpt_code: string;
  avg_gross_charge: number;
}

interface InternationalStatsResult {
  categoryComparison: InternationalCategoryComparison[];
  overallComparison: InternationalOverallComparison[];
  countriesList: InternationalCountry[];
  scenarioCosts: InternationalScenarioCost[];
  procedurePrices: InternationalProcedurePrice[];
  procedureNames: InternationalProcedureName[];
  usPricing: InternationalUsPricing[];
}

export function useStateInternationalStats() {
  const liveDb = useLiveDb();
  const { data, isLoading } = useQuery({
    queryKey: ['intl-stats-v3', liveDb],
    queryFn: async (): Promise<InternationalStatsResult> => {
      // Try static data first (encoded .dat on Capacitor, .json on web)
      try {
        const staticData = await loadStaticData<any>('international_stats.json');
        if (staticData && staticData.overallComparison?.length > 0) {
          if (import.meta.env.DEV) console.log(`[IntlStats] Using static data`);
          return {
            categoryComparison: staticData.categoryComparison || [],
            overallComparison: staticData.overallComparison || [],
            countriesList: staticData.countries || [],
            scenarioCosts: staticData.scenarioCosts || [],
            procedurePrices: staticData.procedurePrices || [],
            procedureNames: staticData.procedureNames || [],
            usPricing: staticData.usPricing || [],
          };
        }
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[IntlStats] Static data error:', e);
      }

      // Only hit Supabase if ?db=live
      if (!liveDb) {
        return { categoryComparison: [], overallComparison: [], countriesList: [], scenarioCosts: [], procedurePrices: [], procedureNames: [], usPricing: [] };
      }

      // Supabase fallback — all queries in parallel
      const [catRes, overallRes, countriesRes, scenarioCostsRes] = await Promise.all([
        supabase.from('international_category_comparison' as any).select('*'),
        supabase.from('international_overall_comparison' as any).select('*'),
        supabase.from('international_countries').select('country_code, country_name, flag_emoji, healthcare_system'),
        supabase.from('international_scenario_costs' as any).select('scenario_id, country_code, mapped_cpt_codes, total_cpt_codes, intl_total_cost, us_national_total_cost'),
      ]);

      if (catRes.error) throw catRes.error;
      if (overallRes.error) throw overallRes.error;
      if (countriesRes.error) throw countriesRes.error;
      if (scenarioCostsRes.error) {
        console.warn('[IntlStats] scenarioCosts query failed:', scenarioCostsRes.error);
      }

      // procedurePrices/procedureNames/usPricing come from static data only (tables not anon-accessible)
      return {
        categoryComparison: (catRes.data || []) as InternationalCategoryComparison[],
        overallComparison: (overallRes.data || []) as InternationalOverallComparison[],
        countriesList: (countriesRes.data || []) as InternationalCountry[],
        scenarioCosts: (scenarioCostsRes.data || []) as InternationalScenarioCost[],
        procedurePrices: [],
        procedureNames: [],
        usPricing: [],
      };
    },
    staleTime: 1000 * 60 * 60,
  });

  // Build country lookup
  const countryMap = new Map<string, InternationalCountry>();
  if (data?.countriesList) {
    for (const c of data.countriesList) countryMap.set(c.country_code, c);
  }

  // Unique categories from the data
  const categories = data?.categoryComparison
    ? [...new Set(data.categoryComparison.map(r => r.category))].sort()
    : [];

  return {
    categoryComparison: data?.categoryComparison || [],
    overallComparison: (data?.overallComparison || []).sort((a, b) =>
      Number(b.us_vs_intl_multiplier) - Number(a.us_vs_intl_multiplier)
    ),
    countries: countryMap,
    categories,
    scenarioCosts: data?.scenarioCosts || [],
    procedurePrices: data?.procedurePrices || [],
    procedureNames: data?.procedureNames || [],
    usPricing: data?.usPricing || [],
    isLoading,
  };
}
